import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { AlertTriangle, ChevronDown, ChevronUp, FileText, CheckCircle, ExternalLink, Info } from "lucide-react";
import { useConsultation } from "@/contexts/ConsultationModalContext";

interface Section {
  id: string;
  title: string;
  englishTitle: string;
  icon: string;
  content: React.ReactNode;
}

function TableSection({ rows }: { rows: { en: string; mn: string; how: string }[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-blue-50 dark:bg-blue-900/20">
            <th className="text-left p-3 border border-blue-100 dark:border-blue-800 font-semibold text-blue-900 dark:text-blue-200 w-1/3">Маягтын асуулт (Англи)</th>
            <th className="text-left p-3 border border-blue-100 dark:border-blue-800 font-semibold text-blue-900 dark:text-blue-200 w-1/4">Монгол орчуулга</th>
            <th className="text-left p-3 border border-blue-100 dark:border-blue-800 font-semibold text-blue-900 dark:text-blue-200">Хэрхэн бөглөх</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white dark:bg-gray-900" : "bg-gray-50 dark:bg-gray-800/50"}>
              <td className="p-3 border border-gray-100 dark:border-gray-700 font-mono text-xs text-gray-700 dark:text-gray-300">{row.en}</td>
              <td className="p-3 border border-gray-100 dark:border-gray-700 font-semibold text-gray-800 dark:text-gray-200">{row.mn}</td>
              <td className="p-3 border border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-400">{row.how}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionCard({ section }: { section: Section }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{section.icon}</span>
          <div>
            <div className="font-bold text-gray-900 dark:text-white">{section.title}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{section.englishTitle}</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="p-5 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
          {section.content}
        </div>
      )}
    </div>
  );
}

export default function DS160GuidePage() {
  const { openConsultation } = useConsultation();
  const sections: Section[] = [
    {
      id: "personal1",
      title: "Хувийн мэдээлэл 1",
      englishTitle: "Personal Information 1",
      icon: "👤",
      content: (
        <TableSection rows={[
          { en: "Surnames", mn: "Овог", how: "Паспорт дээрх Латин үсгийн овгийг бичнэ (жишээ: BOLD)" },
          { en: "Given Names", mn: "Нэр", how: "Паспорт дээрх Латин үсгийн нэрийг бичнэ (жишээ: ENKHJARGAL)" },
          { en: "Full Name in Native Alphabet", mn: "Монгол нэр", how: "Монгол үсгээр бүтэн нэрийг бичнэ" },
          { en: "Have you ever used other names?", mn: "Өөр нэр хэрэглэж байсан уу?", how: "Гэрлэлтийн дараа нэр өөрчилсөн бол YES, үгүй бол NO" },
          { en: "Do you have a telecode?", mn: "Телекод байгаа юу?", how: "Монголчуудад ихэвчлэн хамааралгүй — Does Not Apply" },
          { en: "Sex", mn: "Хүйс", how: "Male (эрэгтэй) / Female (эмэгтэй)" },
          { en: "Marital Status", mn: "Гэр бүлийн байдал", how: "Single (гэрлээгүй), Married (гэрлэсэн), Divorced (салсан), Widowed (бэлэвсэн)" },
          { en: "Date of Birth", mn: "Төрсөн огноо", how: "Сар/Өдөр/Жил хэлбэрээр (жишээ: 05/15/1990)" },
          { en: "Place of Birth — City", mn: "Төрсөн хот", how: "Монгол үсгийг Латинаар бичнэ (жишээ: ULAANBAATAR)" },
          { en: "Place of Birth — Country", mn: "Төрсөн улс", how: "MONGOLIA" },
          { en: "Do you hold any other nationality?", mn: "Өөр улсын иргэншил байгаа уу?", how: "Байхгүй бол NO" },
          { en: "National Identification Number", mn: "Регистрийн дугаар", how: "Монгол иргэний регистрийн дугаарыг оруулна" },
          { en: "U.S. Social Security Number", mn: "АНУ-ын нийгмийн даатгалын дугаар", how: "Байхгүй бол Does Not Apply" },
          { en: "U.S. Taxpayer ID Number", mn: "АНУ-ын татварын дугаар", how: "Байхгүй бол Does Not Apply" },
        ]} />
      ),
    },
    {
      id: "personal2",
      title: "Хувийн мэдээлэл 2",
      englishTitle: "Personal Information 2",
      icon: "🏠",
      content: (
        <TableSection rows={[
          { en: "Address Line 1", mn: "Хаяг", how: "Одоо амьдарч буй хаягийг Латин үсгээр бичнэ" },
          { en: "City", mn: "Хот", how: "ULAANBAATAR" },
          { en: "State/Province", mn: "Аймаг/Муж", how: "Улаанбаатарт бол N/A эсвэл ULAANBAATAR" },
          { en: "Postal Code", mn: "Шуудангийн индекс", how: "Монголын шуудангийн индекс (жишээ: 14200)" },
          { en: "Country", mn: "Улс", how: "MONGOLIA" },
          { en: "Phone Number Type", mn: "Утасны дугаарын төрөл", how: "Mobile (гар утас) — ихэвчлэн Mobile сонгоно" },
          { en: "Phone Number", mn: "Утасны дугаар", how: "Улсын код оруулан бичнэ: +97699XXXXXX" },
          { en: "Email Address", mn: "Имэйл хаяг", how: "Идэвхтэй ашигладаг имэйл хаягаа оруулна" },
        ]} />
      ),
    },
    {
      id: "passport",
      title: "Паспортын мэдээлэл",
      englishTitle: "Passport Information",
      icon: "📘",
      content: (
        <TableSection rows={[
          { en: "Passport/Travel Document Type", mn: "Паспортын төрөл", how: "Regular Passport (энгийн паспорт)" },
          { en: "Passport/Travel Document Number", mn: "Паспортын дугаар", how: "Паспорт дээрх дугаарыг яг таарч бичнэ" },
          { en: "Passport Book Number", mn: "Паспортын номын дугаар", how: "Монгол паспортад ихэвчлэн байхгүй — Does Not Apply" },
          { en: "Country/Authority that Issued Passport", mn: "Паспорт олгосон улс", how: "MONGOLIA" },
          { en: "Issuance Date", mn: "Олгосон огноо", how: "Паспортын олгосон огноо (Сар/Өдөр/Жил)" },
          { en: "Expiration Date", mn: "Дуусах огноо", how: "Паспортын хүчинтэй хугацаа дуусах огноо" },
          { en: "Have you ever lost a passport or had one stolen?", mn: "Паспортаа гээсэн эсвэл хулгайлуулсан уу?", how: "Тийм бол YES, үгүй бол NO" },
        ]} />
      ),
    },
    {
      id: "travel",
      title: "Аяллын мэдээлэл",
      englishTitle: "Travel Information",
      icon: "✈️",
      content: (
        <div className="space-y-4">
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-300">
              <strong>Маш чухал:</strong> Зорилгыг буруу тодорхойлох нь хамгийн нийтлэг татгалзах шалтгаан болдог. Яг үнэн зорилгоо бичнэ үү.
            </p>
          </div>
          <TableSection rows={[
            { en: "Purpose of Trip to the U.S.", mn: "АНУ-д явах зорилго", how: "Tourism (аялал), Visit family/friends (гэр бүл/найзаа зочлох), Business (бизнес) — үнэн зорилгоо сонгоно" },
            { en: "Intended Date of Arrival", mn: "Ирэх төлөвлөсөн огноо", how: "Тасалбар авсан бол яг огноог, үгүй бол ойролцоо огноог оруулна" },
            { en: "Intended Length of Stay in U.S.", mn: "АНУ-д байх хугацаа", how: "Хэдэн өдөр байхаа бичнэ (жишээ: 14 days)" },
            { en: "Address Where You Will Stay", mn: "Байрлах хаяг", how: "Зочид буудлын нэр, хаяг эсвэл хамаатан садны хаяг" },
            { en: "Has your U.S. visa ever been cancelled or revoked?", mn: "АНУ-ын визийг хэзээ нэгэн цагт цуцалсан уу?", how: "Байхгүй бол NO" },
            { en: "Has your U.S. visa ever been refused?", mn: "АНУ-ын визийг татгалзсан уу?", how: "Татгалзсан бол YES, огноо, шалтгааныг тайлбарлана" },
            { en: "Have you ever been to the U.S.?", mn: "АНУ-д өмнө нь очиж байсан уу?", how: "Байвал YES, огноо, хугацааг оруулна" },
            { en: "Are you traveling with anyone?", mn: "Хэн нэгэнтэй хамт аялж байна уу?", how: "Хамт явах хүн байвал YES, мэдээллийг оруулна" },
          ]} />
        </div>
      ),
    },
    {
      id: "previous",
      title: "Өмнөх АНУ-ын аялал",
      englishTitle: "Previous U.S. Travel",
      icon: "🗺️",
      content: (
        <TableSection rows={[
          { en: "Have you ever been in the U.S.?", mn: "АНУ-д өмнө нь байсан уу?", how: "Байвал YES, огноо, хугацааг оруулна" },
          { en: "Have you ever been issued a U.S. Visa?", mn: "АНУ-ын виз авч байсан уу?", how: "Байвал YES, визний дугаар, огноог оруулна" },
          { en: "Do you want to use the same visa type?", mn: "Ижил визний ангилал авмаар байна уу?", how: "Тохиромжтой бол YES" },
          { en: "Have you ever been refused a U.S. Visa?", mn: "АНУ-ын виз татгалзуулж байсан уу?", how: "Байвал YES, шалтгааныг тайлбарлана" },
          { en: "Has anyone ever filed an immigrant petition on your behalf?", mn: "Таны өмнөөс цагаачлалын өргөдөл гаргаж байсан уу?", how: "Байвал YES" },
          { en: "Have you ever overstayed a U.S. Visa?", mn: "АНУ-ын визний хугацааг хэтрүүлж байсан уу?", how: "Байвал YES, тайлбарлана" },
        ]} />
      ),
    },
    {
      id: "contact",
      title: "АНУ дахь холбоо барих хүн",
      englishTitle: "U.S. Point of Contact",
      icon: "📞",
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              Хэрэв АНУ-д холбоо барих хүн байхгүй бол зочид буудлын нэр, хаягийг оруулна.
            </p>
          </div>
          <TableSection rows={[
            { en: "Surname", mn: "Холбоо барих хүний овог", how: "АНУ-д байгаа хамаатан, найз эсвэл зочид буудлын нэр" },
            { en: "Given Name", mn: "Нэр", how: "Тухайн хүний нэр" },
            { en: "Organization Name", mn: "Байгууллагын нэр", how: "Зочид буудлын нэр (хэрэв хамаатан байхгүй бол)" },
            { en: "Relationship to You", mn: "Таны хамаатан", how: "Friend (найз), Relative (хамаатан), Hotel (зочид буудал)" },
            { en: "U.S. Address", mn: "АНУ-ын хаяг", how: "Тухайн хүний эсвэл зочид буудлын хаяг" },
            { en: "Phone Number", mn: "Утасны дугаар", how: "АНУ-ын утасны дугаар" },
            { en: "Email Address", mn: "Имэйл", how: "Тухайн хүний имэйл" },
          ]} />
        </div>
      ),
    },
    {
      id: "family",
      title: "Гэр бүлийн мэдээлэл",
      englishTitle: "Family Information",
      icon: "👨‍👩‍👧",
      content: (
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Эцэг эхийн мэдээлэл</h4>
            <TableSection rows={[
              { en: "Father's Surnames", mn: "Аавын овог", how: "Аавын паспорт дээрх Латин овог" },
              { en: "Father's Given Names", mn: "Аавын нэр", how: "Аавын нэр" },
              { en: "Father's Date of Birth", mn: "Аавын төрсөн огноо", how: "Сар/Өдөр/Жил" },
              { en: "Is your father in the U.S.?", mn: "Аав АНУ-д байгаа юу?", how: "Байвал YES, статусыг оруулна" },
              { en: "Mother's Surnames", mn: "Ээжийн овог", how: "Ээжийн паспорт дээрх Латин овог" },
              { en: "Mother's Given Names", mn: "Ээжийн нэр", how: "Ээжийн нэр" },
              { en: "Mother's Date of Birth", mn: "Ээжийн төрсөн огноо", how: "Сар/Өдөр/Жил" },
              { en: "Is your mother in the U.S.?", mn: "Ээж АНУ-д байгаа юу?", how: "Байвал YES, статусыг оруулна" },
            ]} />
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Гэр бүлийн мэдээлэл</h4>
            <TableSection rows={[
              { en: "Do you have any immediate relatives in the U.S.?", mn: "АНУ-д ойрын хамаатан байгаа юу?", how: "Байвал YES, мэдээллийг оруулна" },
              { en: "Spouse's Surnames", mn: "Нөхөр/эхнэрийн овог", how: "Гэрлэсэн бол нөхөр/эхнэрийн овог" },
              { en: "Spouse's Given Names", mn: "Нөхөр/эхнэрийн нэр", how: "Нөхөр/эхнэрийн нэр" },
              { en: "Spouse's Date of Birth", mn: "Нөхөр/эхнэрийн төрсөн огноо", how: "Сар/Өдөр/Жил" },
              { en: "Spouse's Nationality", mn: "Нөхөр/эхнэрийн иргэншил", how: "MONGOLIAN" },
              { en: "Spouse's Address", mn: "Нөхөр/эхнэрийн хаяг", how: "Хамт амьдардаг бол Same as Applicant" },
            ]} />
          </div>
        </div>
      ),
    },
    {
      id: "work",
      title: "Ажил, Боловсрол, Сургалт",
      englishTitle: "Work/Education/Training",
      icon: "💼",
      content: (
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Ажлын мэдээлэл</h4>
            <TableSection rows={[
              { en: "Primary Occupation", mn: "Үндсэн ажил мэргэжил", how: "Employed (ажилтай), Student (оюутан), Self-Employed (өөрийн бизнес), Retired (тэтгэвэрт), Unemployed (ажилгүй)" },
              { en: "Employer Name", mn: "Ажил олгогчийн нэр", how: "Ажиллаж буй байгууллагын нэр" },
              { en: "Employer Address", mn: "Ажил олгогчийн хаяг", how: "Байгууллагын хаяг" },
              { en: "Employer Phone Number", mn: "Ажил олгогчийн утас", how: "Байгууллагын утас" },
              { en: "Job Title", mn: "Ажлын гарчиг", how: "Таны албан тушаал (жишээ: Engineer, Teacher, Manager)" },
              { en: "Briefly Describe Your Duties", mn: "Ажлын үүргийг товч тайлбарлана уу", how: "2-3 өгүүлбэрээр ажлын үүргийг Англиар тайлбарлана" },
              { en: "Monthly Salary (U.S. Dollars)", mn: "Сарын цалин (АНУ доллар)", how: "Сарын цалингаа долларт хөрвүүлж оруулна" },
              { en: "When did you start work?", mn: "Ажилд орсон огноо", how: "Сар/Жил хэлбэрээр" },
            ]} />
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-3">Боловсролын мэдээлэл</h4>
            <TableSection rows={[
              { en: "Name of Institution", mn: "Сургуулийн нэр", how: "Хамгийн сүүлд төгссөн сургуулийн нэр" },
              { en: "Course of Study", mn: "Мэргэжил", how: "Судалсан мэргэжил" },
              { en: "Date of Attendance (From)", mn: "Элссэн огноо", how: "Сар/Жил" },
              { en: "Date of Attendance (To)", mn: "Төгссөн огноо", how: "Сар/Жил" },
              { en: "Did you graduate?", mn: "Төгссөн үү?", how: "YES / NO" },
            ]} />
          </div>
        </div>
      ),
    },
    {
      id: "security",
      title: "Аюулгүй байдал ба хувийн түүх",
      englishTitle: "Security and Background",
      icon: "🔒",
      content: (
        <div className="space-y-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-300">
              <strong>Анхааруулга:</strong> Эдгээр асуултад үнэнч байх нь маш чухал. Худал хариулт өгвөл виз цуцлагдах, АНУ-д хориглогдох шалтгаан болно. <strong>Ихэнх асуултад NO гэж хариулна.</strong>
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-red-50 dark:bg-red-900/20">
                  <th className="text-left p-3 border border-red-100 dark:border-red-800 font-semibold text-red-900 dark:text-red-200 w-1/2">Маягтын асуулт (Англи)</th>
                  <th className="text-left p-3 border border-red-100 dark:border-red-800 font-semibold text-red-900 dark:text-red-200">Монгол орчуулга</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Do you have a communicable disease of public health significance?", "Олон нийтийн эрүүл мэндэд аюул учруулах халдварт өвчин байгаа юу?"],
                  ["Do you have a mental or physical disorder?", "Сэтгэцийн эсвэл биеийн эмгэг байгаа юу?"],
                  ["Are you or have you ever been a drug abuser or addict?", "Мансууруулах бодис хэрэглэдэг эсвэл хэрэглэж байсан уу?"],
                  ["Have you ever been arrested or convicted for any offense or crime?", "Ямар нэгэн гэмт хэрэг, зөрчлөөр баривчлагдаж байсан уу?"],
                  ["Have you ever violated the terms of a U.S. visa?", "АНУ-ын визний нөхцөлийг зөрчиж байсан уу?"],
                  ["Have you ever been unlawfully present in the U.S.?", "АНУ-д хууль бусаар байсан уу?"],
                  ["Have you ever been deported from the U.S.?", "АНУ-аас хөөгдөж байсан уу?"],
                  ["Have you ever been a member of a terrorist organization?", "Терроризмын байгууллагын гишүүн байсан уу?"],
                  ["Have you ever committed or conspired to commit a terrorist act?", "Терроризмын үйлдэл хийж байсан уу?"],
                  ["Have you ever participated in genocide?", "Геноцидт оролцож байсан уу?"],
                  ["Have you ever been involved in trafficking in persons?", "Хүн худалдаалахад холбоотой байсан уу?"],
                  ["Have you ever been involved in money laundering?", "Мөнгө угаахад холбоотой байсан уу?"],
                ].map(([en, mn], i) => (
                  <tr key={i} className={i % 2 === 0 ? "bg-white dark:bg-gray-900" : "bg-gray-50 dark:bg-gray-800/50"}>
                    <td className="p-3 border border-gray-100 dark:border-gray-700 font-mono text-xs text-gray-700 dark:text-gray-300">{en}</td>
                    <td className="p-3 border border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-200">{mn}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ),
    },
    {
      id: "photo",
      title: "Зураг оруулах шаардлага",
      englishTitle: "Photo Requirements",
      icon: "📷",
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: "✅", text: "Цагаан дэвсгэртэй, нүүрний зураг" },
              { icon: "✅", text: "600×600 пиксел буюу түүнээс дээш" },
              { icon: "✅", text: "JPEG форматтай" },
              { icon: "✅", text: "Сүүлийн 6 сарын дотор авсан" },
              { icon: "✅", text: "Нүд нь тодорхой харагдаж байх" },
              { icon: "❌", text: "Нүдний шил зүүхгүй байх" },
              { icon: "❌", text: "Малгай, толгойн хувцас зүүхгүй байх" },
              { icon: "✅", text: "Шашны толгойн хувцас зөвшөөрнө" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <span className="text-lg">{item.icon}</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  const commonMistakes = [
    "Нэрийг паспорттай таарахгүй бичих",
    "Зорилгыг буруу тодорхойлох — хамгийн нийтлэг татгалзах шалтгаан",
    "Өмнөх татгалзалтыг нуух",
    "АНУ-д хамаатан байгааг нуух",
    "Цалингийн мэдээллийг буруу оруулах",
    "Ажлын байрны мэдээллийг хоосон орхих",
    "Аяллын огноо болон байрлах хаягийг хоосон орхих",
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-white/20 rounded-xl p-3">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="text-blue-200 text-sm font-medium uppercase tracking-wider">АНУ-ын виз</div>
              <h1 className="text-3xl md:text-4xl font-bold">DS-160 Маягт Бөглөх Заавар</h1>
            </div>
          </div>
          <p className="text-blue-100 text-lg mt-4 max-w-2xl">
            АНУ-ын визний онлайн өргөдлийн маягтыг алхам алхмаар Монгол хэлээр тайлбарласан бүрэн заавар.
          </p>
          <a
            href="https://ceac.state.gov/genniv/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6 bg-white text-blue-900 font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-50 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            DS-160 маягт бөглөх (ceac.state.gov)
          </a>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-900 dark:text-amber-200">⚠️ Эхлэхийн өмнө заавал уншина уу</p>
            <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
              Энэхүү заавар нь лавлагааны зорилгоор бэлтгэгдсэн бөгөөд <strong>алдаатай байж болзошгүй</strong> тул маягтаа бөглөсний дараа мэргэжлийн зөвлөхөөр <strong>заавал дахин шалгуулахыг зөвлөж байна.</strong> Буруу, алдаатай, эсвэл зөрчилтэй хариулт оруулбал виз шууд татгалзагдах шалтгаан болно.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">

        {/* What is DS-160 */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mb-8 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <Info className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">DS-160 гэж юу вэ?</h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
            DS-160 нь АНУ-ын визний онлайн өргөдлийн маягт юм. B1/B2 (аялал, бизнес), F1 (сургалт), J1 болон бусад визний ангиллын өргөдөл гаргахад энэ маягтыг бөглөх шаардлагатай. Маягтыг бөглөж дуусгасны дараа <strong>баталгаажуулалтын хуудас (confirmation page)</strong> хэвлэж, ярилцлагадаа авч очно.
          </p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: "Маягт бөглөх сайт", value: "ceac.state.gov", icon: "🌐" },
              { label: "Хэл", value: "Англи хэлээр бөглөнө", icon: "🔤" },
              { label: "Нэр бичих", value: "Паспорттай яг таарч байх ёстой", icon: "📋" },
            ].map((item, i) => (
              <div key={i} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                <div className="text-lg mb-1">{item.icon}</div>
                <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">{item.label}</div>
                <div className="text-sm text-blue-900 dark:text-blue-200 font-semibold">{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Sections */}
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Хэсэг бүрийн заавар</h2>
        <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">Хэсэг дээр дарахад дэлгэрэнгүй мэдээлэл нээгдэнэ.</p>

        {sections.map((section) => (
          <SectionCard key={section.id} section={section} />
        ))}

        {/* After submission */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mt-8 shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Маягт дуусгасны дараа</h2>
          </div>
          <div className="space-y-3">
            {[
              { step: "1", text: "SUBMIT товч дарж маягтыг илгээнэ." },
              { step: "2", text: "Баталгаажуулалтын хуудас (Confirmation Page) хэвлэнэ — энэ хуудсан дээр баркод байна." },
              { step: "3", text: "Ярилцлагын цаг захиалахдаа энэ баркодыг ашиглана." },
              { step: "4", text: "Ярилцлагын өдөр баталгаажуулалтын хуудсыг заавал авч очно." },
            ].map((item) => (
              <div key={item.step} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-sm font-bold flex items-center justify-center flex-shrink-0">
                  {item.step}
                </div>
                <p className="text-gray-600 dark:text-gray-400 text-sm pt-1">{item.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Common Mistakes */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 mt-6 shadow-sm border border-red-100 dark:border-red-900/30">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Нийтлэг алдаанууд</h2>
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">Дараах алдаанаас зайлсхийнэ үү:</p>
          <div className="space-y-2">
            {commonMistakes.map((mistake, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg">
                <span className="text-red-500 font-bold text-sm flex-shrink-0">✗</span>
                <span className="text-sm text-gray-700 dark:text-gray-300">{mistake}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Final Warning + CTA */}
        <div className="bg-gradient-to-br from-blue-900 to-indigo-900 text-white rounded-2xl p-8 mt-8 text-center">
          <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-3">Эцсийн анхааруулга</h3>
          <p className="text-blue-100 text-sm leading-relaxed max-w-xl mx-auto mb-6">
            Энэхүү заавар нь <strong>лавлагааны зорилгоор</strong> бэлтгэгдсэн бөгөөд алдаа агуулж болзошгүй тул маягтаа бөглөсний дараа мэргэжлийн зөвлөхөөр <strong>заавал дахин шалгуулахыг зөвлөж байна.</strong> United Dream Mongolia-ийн мэргэжилтнүүд DS-160 маягтыг шалгах, алдааг засах үйлчилгээ үзүүлдэг.
          </p>
          <a
            href="#" onClick={(e) => { e.preventDefault(); openConsultation(); }}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-blue-900 font-semibold px-6 py-3 rounded-xl hover:bg-blue-50 transition-colors"
          >
            Мэргэжлийн зөвлөгөө авах →
          </a>
        </div>

      </div>

      <Footer />
    </div>
  );
}
